import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeleteInvoiceItemsMutation,
    useInvoiceItemsInfiniteQuery,
} from './useInvoiceItemsQuery';
import {useInvoiceItemsSearch} from './useInvoiceItemsSearch';
import {useInvoiceItemsTableColumns} from './useInvoiceItemsTableColumns';
import {InvoiceItemsFilters} from './useInvoiceItemsFilter';
import {InvoiceItem} from '../types';

/**
 * Default sort: NEWEST FIRST, on `dateAtMillis`.
 *
 * The key must be immutable and non-null. `date: Int!` is set by Stripe when
 * the item is created and never moves, so it satisfies both.
 *
 * ⚠️ The obvious-looking choice, `created`, DOES NOT EXIST on this schema —
 * see types.ts. Defaulting to it would have produced insertion order silently,
 * which is exactly the "nullable key degrades to page-arrival luck" failure the
 * shared sortRecords doc warns about.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<InvoiceItem> = {
    columnAccessor: 'dateAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against. `id` is included because a Stripe
 * invoice-item id (`ii_...`) is what an operator pastes in from the Stripe
 * dashboard when chasing a specific line.
 */
const matchesKeyword = (item: InvoiceItem, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.customerId,
        item.description,
        item.currency,
        item.price,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (item: InvoiceItem, filters: InvoiceItemsFilters): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof InvoiceItem];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listInvoiceItems($filter, $limit, $nextToken)` does take a filter, but it is
 * the INVOICE SCOPE (`{invoice: invoiceId}`) — there is no field-level
 * narrowing and no sort argument, so the server can neither search nor order
 * within one invoice's items. Everything below therefore operates on the loaded
 * window only. Sorting page 1 does not sort the dataset, and a keyword matches
 * only rows the browser has already received.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match".
 *
 * ⚠️ `invoiceId` is REQUIRED — this list only exists in the context of one
 * invoice. The previous hook threw on an undefined id; it now drives `enabled`
 * on the query instead, so a parent that renders before its route param
 * resolves gets an empty list rather than a crash.
 */
export const useInvoiceItems = (
    invoiceId: string,
    filters: InvoiceItemsFilters = {},
) => {
    const query = useInvoiceItemsInfiniteQuery(invoiceId);
    const {columns} = useInvoiceItemsTableColumns();
    const {keyword} = useInvoiceItemsSearch();
    const deleteMutation = useDeleteInvoiceItemsMutation(invoiceId);

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: InvoiceItem[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // Normalise Stripe's epoch-seconds into millis ONCE, here, so the Date
        // column can both render and sort the same value.
        .map(item => ({
            ...item,
            dateAtMillis: typeof item.date === 'number'
                ? item.date * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<InvoiceItem>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched`
    // is rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
